/**
 * Error classification — the single source of truth for "may this be retried?"
 *
 * GUARDRAIL 10 (masterplan v3.2 §2): no silent failures. Every dropped, failed or
 * timed-out background operation produces BOTH a user-visible signal and a ledger
 * entry. This module decides *how* the failure is treated; it never decides whether
 * the user is told. The answer to that is always yes.
 *
 * LESSON PINNED AT THE SCENE (Journal post-mortem §2): classification by message
 * substring is the bug factory. Browser/runtime/proxy vendors rewrite messages
 * between versions and locales; a retry loop keyed on `msg.includes('network')`
 * silently stops retrying the day Safari changes its wording. Therefore the rule
 * order here is: error name -> error code -> HTTP status -> Postgres SQLSTATE ->
 * (last resort) message substring, and a substring match is *recorded as such* in
 * `matchedBy` so it shows up in the ledger and can be promoted to a real rule.
 */
const RETRYABLE_BY_NAME = {
    // Standing rule (project instructions): AbortError/TimeoutError are ALWAYS retryable.
    // Yes, a user-cancelled fetch also throws AbortError. Treating it as retryable
    // costs one redundant request; treating it as fatal costs a silently dropped
    // write, which is the failure mode Guardrail 10 exists to kill. Callers that
    // genuinely cancel on purpose must drop the operation themselves, before it
    // ever reaches the classifier.
    AbortError: 'REQUEST_ABORTED',
    TimeoutError: 'REQUEST_TIMEOUT',
    NetworkError: 'NETWORK_UNAVAILABLE',
};
const RETRYABLE_BY_CODE = {
    ECONNRESET: 'NETWORK_RESET',
    ECONNREFUSED: 'NETWORK_REFUSED',
    ETIMEDOUT: 'NETWORK_TIMEOUT',
    EAI_AGAIN: 'DNS_TEMPORARY',
    EPIPE: 'NETWORK_PIPE',
    UND_ERR_CONNECT_TIMEOUT: 'NETWORK_TIMEOUT',
    UND_ERR_HEADERS_TIMEOUT: 'NETWORK_TIMEOUT',
    ERR_NETWORK: 'NETWORK_UNAVAILABLE',
};
const FATAL_BY_CODE = {
    ENOTFOUND: 'DNS_NOT_FOUND',
    ERR_INVALID_URL: 'BAD_REQUEST',
};
/** HTTP statuses that must NEVER be retried — replaying them cannot succeed. */
const HTTP_FATAL = {
    400: 'BAD_REQUEST',
    401: 'AUTH_EXPIRED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    405: 'METHOD_NOT_ALLOWED',
    409: 'CONFLICT',
    410: 'GONE',
    413: 'PAYLOAD_TOO_LARGE',
    415: 'UNSUPPORTED_MEDIA_TYPE',
    422: 'VALIDATION_FAILED',
};
/** HTTP statuses where replaying the identical request can succeed. */
const HTTP_RETRYABLE = {
    408: 'REQUEST_TIMEOUT',
    425: 'TOO_EARLY',
    429: 'RATE_LIMITED',
    500: 'SERVER_ERROR',
    502: 'BAD_GATEWAY',
    503: 'SERVICE_UNAVAILABLE',
    504: 'GATEWAY_TIMEOUT',
    507: 'INSUFFICIENT_STORAGE',
};
/** Postgres SQLSTATE. Retryable = transient contention/connection, nothing else. */
const PG_RETRYABLE = {
    '40001': 'PG_SERIALIZATION_FAILURE',
    '40P01': 'PG_DEADLOCK',
    '53300': 'PG_TOO_MANY_CONNECTIONS',
    '57P01': 'PG_ADMIN_SHUTDOWN',
    '57P03': 'PG_CANNOT_CONNECT_NOW',
    '08000': 'PG_CONNECTION_EXCEPTION',
    '08006': 'PG_CONNECTION_FAILURE',
};
const PG_FATAL = {
    '23502': 'PG_NOT_NULL_VIOLATION',
    '23503': 'PG_FOREIGN_KEY_VIOLATION',
    '23505': 'PG_UNIQUE_VIOLATION',
    '23514': 'PG_CHECK_VIOLATION',
    '42501': 'PG_INSUFFICIENT_PRIVILEGE', // RLS denial — see readEmptyIsNotAbsent()
    '42703': 'PG_UNDEFINED_COLUMN',
    '42P01': 'PG_UNDEFINED_TABLE',
};
/**
 * Message substrings — LAST RESORT ONLY. Every hit here is reported as
 * `matchedBy: 'messageFallback'` so the ledger shows how often we are guessing.
 * If a substring fires often, find the real name/code and promote it above.
 */
const MESSAGE_FALLBACK = [
    [/failed to fetch/i, 'NETWORK_UNAVAILABLE', true],
    [/network ?error/i, 'NETWORK_UNAVAILABLE', true],
    [/load failed/i, 'NETWORK_UNAVAILABLE', true], // Safari's wording for a dead fetch
    [/socket hang ?up/i, 'NETWORK_RESET', true],
];
const USER_MESSAGES = {
    REQUEST_ABORTED: 'That took too long and was interrupted. It will be retried.',
    REQUEST_TIMEOUT: 'The connection timed out. It will be retried.',
    NETWORK_UNAVAILABLE: 'No connection right now. It will be retried when you are back online.',
    NETWORK_RESET: 'The connection dropped. It will be retried.',
    NETWORK_REFUSED: 'Could not reach the server. It will be retried.',
    NETWORK_TIMEOUT: 'The connection timed out. It will be retried.',
    NETWORK_PIPE: 'The connection dropped. It will be retried.',
    DNS_TEMPORARY: 'Could not reach the server. It will be retried.',
    DNS_NOT_FOUND: 'Could not reach the server. Support has been notified.',
    RATE_LIMITED: 'Too many requests at once. It will be retried shortly.',
    SERVER_ERROR: 'The server had a problem. It will be retried.',
    BAD_GATEWAY: 'The server had a problem. It will be retried.',
    SERVICE_UNAVAILABLE: 'The server is temporarily unavailable. It will be retried.',
    GATEWAY_TIMEOUT: 'The server took too long. It will be retried.',
    INSUFFICIENT_STORAGE: 'The server ran out of space. Support has been notified.',
    TOO_EARLY: 'The server asked us to wait. It will be retried.',
    AUTH_EXPIRED: 'Your session expired. Please sign in again — nothing was lost.',
    FORBIDDEN: 'You do not have permission for that. Nothing was changed.',
    NOT_FOUND: 'That item no longer exists. Nothing was changed.',
    CONFLICT: 'Someone else changed this first. Open it again to see the current version.',
    GONE: 'That item no longer exists. Nothing was changed.',
    BAD_REQUEST: 'That could not be saved. Support has been notified.',
    METHOD_NOT_ALLOWED: 'That could not be saved. Support has been notified.',
    VALIDATION_FAILED: 'Some fields were not accepted. Check the highlighted fields.',
    PAYLOAD_TOO_LARGE: 'That file is too large to upload.',
    UNSUPPORTED_MEDIA_TYPE: 'That file type is not supported.',
    PG_UNIQUE_VIOLATION: 'That already exists. Nothing was changed.',
    PG_SERIALIZATION_FAILURE: 'The server was busy. It will be retried.',
    PG_DEADLOCK: 'The server was busy. It will be retried.',
    PG_TOO_MANY_CONNECTIONS: 'The server is busy. It will be retried.',
    PG_ADMIN_SHUTDOWN: 'The server restarted. It will be retried.',
    PG_CANNOT_CONNECT_NOW: 'The server is starting up. It will be retried.',
    PG_CONNECTION_EXCEPTION: 'The server connection dropped. It will be retried.',
    PG_CONNECTION_FAILURE: 'The server connection dropped. It will be retried.',
    PG_INSUFFICIENT_PRIVILEGE: 'You do not have permission for that. Nothing was changed.',
    UNKNOWN: 'Something went wrong and was not saved. Support has been notified.',
};
/** Literal, not a lookup: under `noUncheckedIndexedAccess` a Record lookup is
 *  itself optional, so the fallback must not be one. */
const UNKNOWN_USER_MESSAGE = 'Something went wrong and was not saved. Support has been notified.';
function userMessageFor(code) {
    return USER_MESSAGES[code] ?? UNKNOWN_USER_MESSAGE;
}
function readString(obj, key) {
    if (typeof obj !== 'object' || obj === null)
        return undefined;
    const v = obj[key];
    return typeof v === 'string' ? v : undefined;
}
function readNumber(obj, key) {
    if (typeof obj !== 'object' || obj === null)
        return undefined;
    const v = obj[key];
    if (typeof v === 'number' && Number.isFinite(v))
        return v;
    if (typeof v === 'string' && /^\d+$/.test(v))
        return Number(v);
    return undefined;
}
/** Retry-After is either delta-seconds or an HTTP-date. Both are handled. */
export function parseRetryAfter(value, now = new Date()) {
    if (!value)
        return undefined;
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0)
        return Math.round(seconds * 1000);
    const when = Date.parse(value);
    if (Number.isNaN(when))
        return undefined;
    return Math.max(0, when - now.getTime());
}
/**
 * Classify any throwable into a retry decision.
 * Pure and synchronous by design: it is called on every failure path, including
 * inside the ledger writer, and must never itself throw.
 */
export function classifyError(error, options = {}) {
    const base = {
        original: error,
        requiresReauth: false,
        retryAfterMs: parseRetryAfter(options.retryAfter),
    };
    const name = readString(error, 'name');
    const rawCode = readString(error, 'code');
    const message = readString(error, 'message') ?? (typeof error === 'string' ? error : '');
    const httpStatus = options.httpStatus ??
        readNumber(error, 'status') ??
        readNumber(error, 'statusCode') ??
        readNumber(error, 'httpStatus');
    const pgCode = rawCode && /^[0-9A-Z]{5}$/.test(rawCode) ? rawCode : readString(error, 'sqlState');
    // 1. error.name
    const byName = name ? RETRYABLE_BY_NAME[name] : undefined;
    if (byName) {
        const code = byName;
        return { ...base, code, retryable: true, transport: 'network', matchedBy: 'name', userMessage: userMessageFor(code) };
    }
    // 2. error.code (node/undici/browser style)
    const retryableByCode = rawCode ? RETRYABLE_BY_CODE[rawCode] : undefined;
    if (retryableByCode) {
        const code = retryableByCode;
        return { ...base, code, retryable: true, transport: 'network', matchedBy: 'code', userMessage: userMessageFor(code) };
    }
    const fatalByCode = rawCode ? FATAL_BY_CODE[rawCode] : undefined;
    if (fatalByCode) {
        const code = fatalByCode;
        return { ...base, code, retryable: false, transport: 'network', matchedBy: 'code', userMessage: userMessageFor(code) };
    }
    // 3. HTTP status
    if (typeof httpStatus === 'number') {
        const httpFatal = HTTP_FATAL[httpStatus];
        if (httpFatal) {
            const code = httpFatal;
            return {
                ...base,
                code,
                retryable: false,
                transport: 'http',
                matchedBy: 'httpStatus',
                httpStatus,
                requiresReauth: httpStatus === 401,
                userMessage: userMessageFor(code),
            };
        }
        const httpRetryable = HTTP_RETRYABLE[httpStatus];
        if (httpRetryable) {
            const code = httpRetryable;
            return { ...base, code, retryable: true, transport: 'http', matchedBy: 'httpStatus', httpStatus, userMessage: userMessageFor(code) };
        }
        // Unmapped 4xx is a client mistake: never replay it.
        if (httpStatus >= 400 && httpStatus < 500) {
            return { ...base, code: 'BAD_REQUEST', retryable: false, transport: 'http', matchedBy: 'httpStatus', httpStatus, userMessage: userMessageFor('BAD_REQUEST') };
        }
        if (httpStatus >= 500) {
            return { ...base, code: 'SERVER_ERROR', retryable: true, transport: 'http', matchedBy: 'httpStatus', httpStatus, userMessage: userMessageFor('SERVER_ERROR') };
        }
    }
    // 4. Postgres SQLSTATE
    const pgRetryable = pgCode ? PG_RETRYABLE[pgCode] : undefined;
    if (pgRetryable) {
        const code = pgRetryable;
        return { ...base, code, retryable: true, transport: 'postgres', matchedBy: 'pgCode', pgCode, userMessage: userMessageFor(code) };
    }
    const pgFatal = pgCode ? PG_FATAL[pgCode] : undefined;
    if (pgFatal) {
        const code = pgFatal;
        return { ...base, code, retryable: false, transport: 'postgres', matchedBy: 'pgCode', pgCode, userMessage: userMessageFor(code) };
    }
    // 5. Message substrings — last resort, and it says so.
    for (const [pattern, code, retryable] of MESSAGE_FALLBACK) {
        if (pattern.test(message)) {
            return { ...base, code, retryable, transport: 'network', matchedBy: 'messageFallback', userMessage: userMessageFor(code) };
        }
    }
    // 6. Default: unknown. NOT retried (a blind replay loop on an unknown fault is
    // how you double-write), but always surfaced and always logged.
    return { ...base, code: 'UNKNOWN', retryable: false, transport: 'unknown', matchedBy: 'default', userMessage: userMessageFor('UNKNOWN') };
}
export function isRetryable(error, options) {
    return classifyError(error, options).retryable;
}
/**
 * RLS READ SEMANTICS (project instructions, "Data reads & RLS").
 * An empty PostgREST/RLS result is HTTP 200. It means "no rows visible to this
 * session", which is NOT the same as "no rows exist" — an expired session sees
 * nothing and reports success. Any caller that is about to act on an empty read
 * of data that may exist must first prove the session is fresh. This helper is
 * the explicit decision point so the check cannot be forgotten silently.
 */
export function emptyReadNeedsSessionCheck(rowCount, mayExist) {
    return rowCount === 0 && mayExist;
}
//# sourceMappingURL=classify.js.map